/**
 * Dexie wrapper for the Message Vault.
 *
 * One database. One table. No revisions, no events, no state machine.
 *
 *   messages
 *     ─ msgId        (unique, primary lookup key)
 *     ─ chatId       (for per-chat queries / wipe-this-chat)
 *     ─ capturedAt   (for retention sweeps)
 */
import Dexie, { type Table } from 'dexie';
import type { BackedUpMessage, PlaceholderMapping } from './types';
import { canonicalMsgKey } from './resolve-msg-id';

export const VAULT_DB_NAME = 'blueticks-message-vault';

/** v5 `messages` store schema string (exported so it can be asserted in
 *  tests without opening IndexedDB).
 *
 *  Trimmed from v4: the standalone `senderId`, `type`, `whatsappTimestamp`
 *  and `synced` indexes are dropped — NOTHING queries them (context matching
 *  filters in JS; the cascade + uploader use the `[chatId+whatsappTimestamp]`
 *  and `[synced+capturedAt]` COMPOUNDS, whose prefixes already serve any
 *  `where('chatId')`/`where('synced')`). Every dropped index was maintained
 *  on each capture for no reader → pure write amplification. Kept indexes:
 *  `&msgId` (unique), `chatId`, `capturedAt` (retention range delete),
 *  `mediaSynced` (media-phase queue), and the two compounds. */
export const VAULT_V5_MESSAGES_SCHEMA =
  '++id, &msgId, chatId, capturedAt, mediaSynced, [chatId+capturedAt], [chatId+whatsappTimestamp], [synced+capturedAt]';

/** v6 — adds `dedupKey` (addressing-agnostic `<fromMe>_<stanzaId>`), the index
 *  the capture path checks to collapse a message's @c.us/@lid variants into one
 *  row. Non-unique on purpose: `&msgId` stays the unique primary key so the v6
 *  index build can't fail on pre-existing dups; uniqueness on the canonical key
 *  is enforced at write time (check-before-add) + the v6 upgrade collapses the
 *  historical dups. */
export const VAULT_V6_MESSAGES_SCHEMA =
  '++id, &msgId, dedupKey, chatId, capturedAt, mediaSynced, [chatId+capturedAt], [chatId+whatsappTimestamp], [synced+capturedAt]';

class MessageVaultDB extends Dexie {
  messages!: Table<BackedUpMessage, number>;
  placeholderMappings!: Table<PlaceholderMapping, number>;

  constructor() {
    super(VAULT_DB_NAME);

    // v1 — initial schema. `&msgId` UNIQUE → idempotent capture.
    this.version(1).stores({
      messages: '++id, &msgId, chatId, capturedAt, [chatId+capturedAt]',
    });

    // v2 — adds placeholderMappings (DOM data-id → body).
    this.version(2).stores({
      messages: '++id, &msgId, chatId, capturedAt, [chatId+capturedAt]',
      placeholderMappings:
        '++id, &placeholderDataId, chatId, recordedAt',
    });

    // v3 — adds senderId + type fields + matching indexes.
    //
    // Per PHASE 6 of the spec: indexes on messageKey (already &msgId),
    // chatId (already), AND whatsappTimestamp (new) so the context-based
    // matching can do O(log n) range queries instead of a full scan.
    //
    // `senderId` and `type` are also indexed so the matching fallback
    // can filter candidates quickly when chats are very large.
    //
    // The compound `[chatId+whatsappTimestamp]` is the workhorse for
    // "find captures in chat C between time T1 and T2" used by the
    // context-based matcher.
    //
    // Dexie migrations on additive index changes are safe — existing
    // rows keep their fields, the new indexes just don't cover old
    // rows that lack the new columns. To keep matching consistent
    // we backfill in the upgrade callback.
    this.version(3).stores({
      messages:
        '++id, &msgId, chatId, senderId, type, capturedAt, whatsappTimestamp, [chatId+capturedAt], [chatId+whatsappTimestamp]',
      placeholderMappings:
        '++id, &placeholderDataId, chatId, recordedAt',
    }).upgrade(async (tx) => {
      // Backfill: ensure every existing row has senderId/type/fromMe
      // so the indexes cover them too. Empty strings are fine —
      // matching just won't filter on these dimensions for old rows.
      await tx.table('messages').toCollection().modify((row: any) => {
        if (typeof row.senderId !== 'string') row.senderId = '';
        if (typeof row.type !== 'string') row.type = '';
        if (typeof row.fromMe !== 'boolean') row.fromMe = false;
      });
    });

    // v4 — backup sync flags (Track 1: message-vault backup).
    //
    // Adds `synced` + `mediaSynced` (0|1) so the daily server-backup
    // uploader can select its work queue by FLAG rather than timestamp
    // (see the design doc — flag-based selection is what makes the
    // sync gap-free and duplicate-free). `whatsappTimestamp` gains its
    // own single index for the cascade's per-chat range lookup, and the
    // compound `[synced+capturedAt]` is the uploader's queue index
    // (unsynced rows, chunked by capture order).
    //
    // Backfill: every pre-v4 row is stamped `synced=0`/`mediaSynced=0`
    // ("needs backup") via the same normalizer used at capture time, so
    // the historical vault gets swept up into the first backup run.
    this.version(4).stores({
      messages:
        '++id, &msgId, chatId, senderId, type, capturedAt, whatsappTimestamp, synced, mediaSynced, [chatId+capturedAt], [chatId+whatsappTimestamp], [synced+capturedAt]',
      placeholderMappings:
        '++id, &placeholderDataId, chatId, recordedAt',
    }).upgrade(async (tx) => {
      await tx.table('messages').toCollection().modify((row: any) => {
        if (row.synced !== 0 && row.synced !== 1) row.synced = 0;
        if (row.mediaSynced !== 0 && row.mediaSynced !== 1) row.mediaSynced = 0;
      });
    });

    // v5 — drop 4 never-queried indexes (write-amp) + normalize mediaSynced.
    //
    // Removing an index leaves the row DATA intact (senderId/type/etc. stay on
    // rows), so no data migration is needed there. The upgrade only re-stamps
    // `mediaSynced`: byte-less rows (no audioBase64) → 1 ("nothing to upload")
    // so the uploader's media-phase query stops scanning the whole vault.
    this.version(5).stores({
      messages: VAULT_V5_MESSAGES_SCHEMA,
      placeholderMappings:
        '++id, &placeholderDataId, chatId, recordedAt',
    }).upgrade(async (tx) => {
      await tx.table('messages').toCollection().modify((row: any) => {
        if (row.mediaSynced !== 1 && !row.audioBase64) row.mediaSynced = 1;
      });
    });

    // v6 — dedupKey + collapse the @c.us/@lid double-capture.
    //
    // Backfill `dedupKey = canonicalMsgKey(msgId)` on every row, then delete the
    // redundant rows so each message is stored ONCE. WhatsApp addresses a 1:1
    // chat under both the phone `@c.us` JID and the linked-id `@lid` JID, so the
    // same message was captured twice with different `msgId`s (same stanza id).
    // When collapsing a dup group we KEEP the `@c.us`-addressed row (the
    // human-readable phone form); ties fall back to the lowest auto-id (first
    // captured). Going forward, `captureMessage` checks `dedupKey` before insert.
    this.version(6).stores({
      messages: VAULT_V6_MESSAGES_SCHEMA,
      placeholderMappings:
        '++id, &placeholderDataId, chatId, recordedAt',
    }).upgrade(async (tx) => {
      const table = tx.table('messages');
      const rows: any[] = await table.toArray();
      const keep = new Map<string, any>();
      const deleteIds: number[] = [];
      for (const r of rows) {
        const dk = canonicalMsgKey(typeof r.msgId === 'string' ? r.msgId : '');
        r.dedupKey = dk;
        if (!dk) continue; // malformed → leave as its own row
        const winner = keep.get(dk);
        if (!winner) {
          keep.set(dk, r);
          continue;
        }
        // Prefer the @c.us-addressed row; else keep the earliest (lowest id).
        const rCus = String(r.chatId || '').includes('@c.us');
        const wCus = String(winner.chatId || '').includes('@c.us');
        if (rCus && !wCus) {
          deleteIds.push(winner.id);
          keep.set(dk, r);
        } else {
          deleteIds.push(r.id);
        }
      }
      // Stamp dedupKey on every surviving row, then drop the collapsed dups.
      await table.toCollection().modify((row: any) => {
        row.dedupKey = canonicalMsgKey(typeof row.msgId === 'string' ? row.msgId : '');
      });
      if (deleteIds.length > 0) await table.bulkDelete(deleteIds);
    });
  }
}

/** Lazily-opened singleton — IndexedDB might not exist in some
 *  contexts (tests, SSR). Caller code is fully defensive. */
let _instance: MessageVaultDB | null = null;

export const getMessageVaultDb = (): MessageVaultDB => {
  if (_instance) return _instance;
  if (typeof indexedDB === 'undefined') {
    throw new Error(
      '[message-vault] IndexedDB is not available in this context.',
    );
  }
  _instance = new MessageVaultDB();
  return _instance;
};

export const closeMessageVaultDb = async (): Promise<void> => {
  if (!_instance) return;
  await _instance.close();
  _instance = null;
};
